import React, { useState } from 'react';
import { Typography, Grid, Box, Divider, Button } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BoardCard from './BoardCard';
import CustomPagination from '../../../components/CustomPagination';
import BoardsModal from './BoardsModal';

/**
 * Componente para mostrar una categoría de tableros con paginación.
 * 
 * Implementa el principio de responsabilidad única (SRP) de SOLID al encargarse
 * exclusivamente de la presentación y gestión de una categoría específica de tableros.
 * También aplica el principio de segregación de interfaces (ISP) al recibir solo
 * las propiedades necesarias para su funcionamiento.
 *
 * @param {string} title - Título de la categoría de tableros
 * @param {Array} boards - Lista de tableros a mostrar en esta categoría
 * @param {Function} onTogglePinned - Función para alternar el estado de fijación de un tablero
 * @param {Array} pinnedBoards - Lista de tableros fijados/favoritos
 * @param {Function} onViewDetails - Función para mostrar los detalles de un tablero
 */
const BoardCategory = ({ title, boards, onTogglePinned, pinnedBoards, onViewDetails }) => {
  // Estados para la paginación y el modal
  const [page, setPage] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  
  // Configuración de paginación
  const rowsPerPage = 4; // Número de tableros por página en la vista principal

  /**
   * Calcula los tableros a mostrar en la página actual aplicando paginación
   * Esto mejora el rendimiento al renderizar solo los elementos necesarios
   */
  const displayedBoards = boards.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  
  /**
   * Funciones para gestionar el estado del modal
   * Siguen el principio de encapsulamiento al mantener la lógica de estado interna
   */
  const handleOpenModal = () => setModalOpen(true);
  const handleCloseModal = () => setModalOpen(false);

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h4" sx={{ mb: 2, fontWeight: 500 }}>
        {title}
      </Typography>
      <Divider sx={{ mb: 3 }} />
      
      <Grid container spacing={3}>
        {displayedBoards.map((board) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={board.id}>
            <BoardCard 
              board={board} 
              onTogglePinned={onTogglePinned}
              isPinned={pinnedBoards.some(b => b.id === board.id)}
              onViewDetails={onViewDetails}
            />
          </Grid>
        ))}
      </Grid>
      
      {/* Contenedor para paginación y botón Ver todos */}
      <Box display="flex" justifyContent="flex-end" alignItems="center">
        {/* Componente de paginación reutilizable */}
        <CustomPagination
          count={boards.length}
          rowsPerPage={rowsPerPage}
          page={page}
          setPage={setPage}
        />
        {/* Botón para abrir el modal con todos los tableros */}
        <Button
          variant="text"
          color="primary"
          startIcon={<VisibilityIcon />}
          onClick={handleOpenModal}
          sx={{ ml: 2 }}
          aria-label="Ver todos los tableros"
        >
          Ver todos
        </Button>
      </Box>
      
      {/* Modal para ver todos los tableros - se renderiza condicionalmente para mejorar rendimiento */}
      {modalOpen && (
        <BoardsModal 
          open={modalOpen} 
          onClose={handleCloseModal} 
          title={`${title} - Todos los tableros`}
          boards={boards}
          onTogglePinned={onTogglePinned}
          pinnedBoards={pinnedBoards}
          onViewDetails={onViewDetails}
        />
      )}
    </Box>
  );
};

export default BoardCategory;